import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { findNodesByType } from "@iventis/tree-browser";
import { compareAsc } from "date-fns";
import type { SidebarOrderLayer } from "./sidebar-order-helpers";

/** The layers of a stream sit above its stages, so a node's type decides where it goes before any order does */
const getNodeTypePrecedence = (node: Node) => (node.type === NodeType.Layer ? 0 : 1);

/** The layer whose sidebar order and creation decide where a stage sits, which is the first layer inside it */
const findFirstDescendantLayer = (node: Node, layers: SidebarOrderLayer[]): SidebarOrderLayer => {
    const layerNodes = findNodesByType([node], NodeType.Layer);
    return layerNodes.reduce<SidebarOrderLayer>((first, layerNode) => first ?? layers.find((l) => l.id === layerNode.id), null);
};

const getLayerOrder = (layer: SidebarOrderLayer) => layer?.sidebarOrder ?? Infinity;

/** Compares two layers the way the rest of the sidebar does: sidebarOrder, then creation for those without one */
const compareLayers = (a: SidebarOrderLayer, b: SidebarOrderLayer) => {
    const aOrder = getLayerOrder(a);
    const bOrder = getLayerOrder(b);
    if (aOrder !== bOrder) {
        return aOrder - bOrder;
    }
    // A newly created stage's layers arrive without a sidebar order, so creation is what puts them after the existing ones
    return a?.createdAt != null && b?.createdAt != null ? compareAsc(a.createdAt, b.createdAt) : 0;
};

/**
 * Orders what a stream shows: its own layers (transfers, breaks) first, then its stages in the order of the layers they
 * hold, which is what the backend renumbers when the timetable moves a stage
 */
export const sortStreamChildNodes = (children: Node[], layers: SidebarOrderLayer[]) =>
    [...children].sort((a, b) => {
        const typeDifference = getNodeTypePrecedence(a) - getNodeTypePrecedence(b);
        if (typeDifference !== 0) {
            return typeDifference;
        }
        if (a.type === NodeType.Layer) {
            return compareLayers(
                layers.find((l) => l.id === a.id),
                layers.find((l) => l.id === b.id)
            );
        }
        return compareLayers(findFirstDescendantLayer(a, layers), findFirstDescendantLayer(b, layers));
    });

/** The stages of a stream in the order the sidebar shows them, which is also the order their badges number them in */
export const getOrderedStageNodes = (streamNode: Node, layers: SidebarOrderLayer[]) =>
    sortStreamChildNodes(streamNode?.childNodes ?? [], layers).filter((node) => node.type === NodeType.Stage);
